import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Popconfirm, Row, Space, Table, Tag, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from '../../../../app/hooks';
import { selectUser } from '../../../../auth/states/userSlice';
import { IPermission } from '../../../../common/types/commonTypes';
import { GetPayMethod } from '../../../../common/utils/CommonUtils';
import { debounce, Fixed2 } from '../../../../common/utils/common.utils';
import usePagination from '../../../../common/utils/usePagination';
import useUrlParams from '../../../../common/utils/useUrlParams';
import { DateInput, FormInput } from '../../../../components/common/FormItem/FormItems';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import Reload from '../../../../components/common/reload/Reload';
import LoadingIndicator from '../../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../../helpers/healper';
import { IMoneyReceiptViewGetAllType } from '../../Types/MoneyReceiptTypes';
import {
  useDeleteMoneyReturnMutation,
  useGetAllMoneyReceiptsV2Query,
} from '../../api/endpoints/moneyReceiptEndpoints';

type Props = { permission?: IPermission };

interface DataType {
  key: React.Key;
  receipt_has_deleted: number;
  trxntype_name: string;
  receipt_vouchar_no: string;
  receipt_payment_date: string;
  acctype_name: string;
  client_name: string;
  receipt_total_amount: number;
  receipt_id: number;
  account_name?: string;
  receipt_money_receipt_no?: string | null;
  cheque_or_bank_name?: string;
  mobile?: string;
}

const ListofMoneyReceipt = ({ permission }: Props) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const user = useAppSelector(selectUser);

  const {
    data: moneyReceipts,
    isFetching,
    isLoading,
    refetch,
    isLoading: dataIsLoading,
  } = useGetAllMoneyReceiptsV2Query({
    ...getAllUrlParams(),
    page,
    size,
  });
  const [deleteMoneyReceipt, { isLoading: deleteLoading, isSuccess, isError }] =
    useDeleteMoneyReturnMutation();

  const handleDelete = async (values: IMoneyReceiptViewGetAllType, index: number) => {
    setColumnIndex(index);
    await deleteMoneyReceipt({
      id: values.receipt_id,
      userId: user?.user_id as number,
    });
  };
  useEffect(() => {
    if (isSuccess) {
      setColumnIndex(null);
    } else if (isError) {
      setColumnIndex(null);
    }
  }, [isSuccess, isError]);
  const columns: ColumnsType<any> = [
    {
      title: 'SL.',
      render: (_, data, index) => (
        <>{((pagination.current || 1) - 1) * (pagination.pageSize || 50) + 1 + index}</>
      ),
    },

    {
      title: 'Payment Date',
      dataIndex: 'receipt_payment_date',
      key: 'receipt_payment_date',
      align: 'left',
      render: (curr) => dayjs(curr).format('DD-MM-YYYY'),
    },

    {
      title: 'Receipt No',
      dataIndex: 'receipt_vouchar_no',
      key: 'receipt_vouchar_no',
      align: 'left',
    },
    {
      title: 'Manual Receipt No',
      dataIndex: 'receipt_money_receipt_no',
      key: 'receipt_money_receipt_no',
      align: 'left',
    },

    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        return (
          <Link
            to={
              record.client_id
                ? `/reports/client_ledger?client_query=${record.client_id}`
                : `/reports/combined_ledger?id=${record.combined_id}`
            }
          >
            <span>{record.client_name}</span>
          </Link>
        );
      },
      align: 'left',
    },

    {
      title: 'Account Name',
      dataIndex: 'cheque_or_bank_name',
      key: 'cheque_or_bank_name',
      align: 'left',
      render: (_, record) => {
        let color = record.cheque_or_bank_name === 'DEPOSIT' ? 'green' : 'red';

        return record.cheque_or_bank_name == 'DEPOSIT' ||
          record.cheque_or_bank_name == 'PENDING' ||
          record.cheque_or_bank_name == 'BOUNCE' ? (
          <Tag color={color}>{record.cheque_or_bank_name}</Tag>
        ) : (
          <Link to={`/accounts/account_statement/${record.account_id}`}>{record.account_name}</Link>
        );
      },
    },

    {
      title: 'Payment Method',
      key: 'acctype_name',
      dataIndex: 'acctype_name',
      render: (field) => <GetPayMethod field={field} />,
    },

    {
      title: 'Received Amount',
      dataIndex: 'receipt_total_amount',
      key: 'receipt_total_amount',
      align: 'right',
      render: (curr) => Fixed2(curr).toLocaleString(),
    },

    {
      title: 'Discount Amount',
      dataIndex: 'receipt_total_discount',
      key: 'receipt_total_discount',
      align: 'right',
      render: (curr) => Fixed2(curr).toLocaleString(),
    },

    {
      title: 'Received By',
      key: 'employee_full_name',
      dataIndex: 'employee_full_name',
    },

    {
      title: 'Prepared By',
      key: 'user_full_name',
      dataIndex: 'user_full_name',
    },

    {
      title: 'Action',
      key: 'operation',
      width: 100,
      render: (_, data, index) => (
        <Space size='small'>
          {permission?.['read:any'] && (
            <Link to={`/moneyreceipt/view/${data.receipt_id}`} state={location.pathname}>
              <Button size='small' type='primary'>
                <Typography style={{ color: '#ffffff' }}>View</Typography>
              </Button>
            </Link>
          )}

          {permission?.['update:any'] && data.cheque_or_bank_name !== 'DEPOSIT' && (
            <Link to={`/moneyreceipt/edit/${data.receipt_id}`} state={location.pathname}>
              <Button size='small' type='primary'>
                Edit
              </Button>
            </Link>
          )}
          {permission?.['delete:any'] && (
            <Popconfirm
              placement='topLeft'
              title='Sure to delete?'
              onConfirm={() => handleDelete(data, index)}
            >
              <Button size='small' type='primary' danger loading={columnIndex === index && true}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Handle Search Operation

  const dataWithKey = moneyReceipts?.data?.data?.map((item, index) => ({
    key: String(index),
    ...item,
  }));
  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search,
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);
  return (
    <>
      {(isLoading || isFetching) && <LoadingIndicator />}

      <BreadCrumb arrOfOption={['Money Receipts']} />
      {/* HEADER */}
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={10}>
            {permission?.['create:any'] && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Link to='/moneyreceipt/add' state={location.pathname}>
                  <Button type='primary' icon={<PlusOutlined />} style={{ width: '100%' }}>
                    Create Money Receipt
                  </Button>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload
                  refetch={() => {
                    refetch();
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <DateInput
                name={'date_range'}
                rangePicker
                size={8}
                noStyle
                onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
              />
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      <div>
        <span style={{ float: 'left', marginBottom: '1rem' }}>
          You have total
          <strong>
            {' '}
            {moneyReceipts?.count ? moneyReceipts?.count : moneyReceipts?.data?.data?.length}{' '}
          </strong>
          rows
        </span>
        <Table
          scroll={{ x: 'max-content' }}
          size='small'
          bordered
          rowKey={(e) => e.receipt_vouchar_no}
          columns={columns}
          dataSource={dataWithKey}
          pagination={changePagination(moneyReceipts?.count)}
          summary={(_) => {
            return (
              <Table.Summary.Row style={{ background: '#031c291f', height: 40 }}>
                <Table.Summary.Cell index={1} colSpan={1} />
                <Table.Summary.Cell index={0} colSpan={6}>
                  <Typography.Text strong>
                    <div className='report_total_footer'>
                      <Typography.Text strong>TOTAL RECEIVED </Typography.Text>
                    </div>
                  </Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={1}>
                  <div>
                    <strong>{moneyReceipts?.data?.total_received}</strong>
                  </div>
                </Table.Summary.Cell>

                <Table.Summary.Cell index={1} colSpan={5} />
              </Table.Summary.Row>
            );
          }}
        />
      </div>
    </>
  );
};

export default ListofMoneyReceipt;

const ViewInvoiceStyle = styled.div`
  .transfer-in-list {
    width: 100%;
  }

  .transfer-in-list .transfer-in-list-td > td {
    padding: 12px !important;
    border-bottom: 1px solid lightgray;
    font-size: 13px !important;
  }

  .transfer-in-list .ant-table-thead .ant-table-cell {
    padding: 10px !important;
    background-color: #e2e2e2;
    font-size: 13px !important;
    border-bottom: 1px solid lightgray;
  }
`;
